import { useMemo, useState } from 'react'
import {
  DECADE_BUCKETS,
  DEFAULT_FILTERS,
  RUNTIME_MAX,
  RUNTIME_MIN,
  YEAR_MAX,
  YEAR_MIN,
  isFiltersDirty,
  type LibraryDetailsEntry,
  type LibraryFilters,
  type MovieSortKey,
  type SeriesSortKey,
} from './useFilterSort'

interface Props {
  scope: 'movies' | 'series'
  filters: LibraryFilters
  onFiltersChange: (next: LibraryFilters) => void
  movieSort: MovieSortKey
  onMovieSortChange: (next: MovieSortKey) => void
  seriesSort: SeriesSortKey
  onSeriesSortChange: (next: SeriesSortKey) => void
  detailsIndex?: Map<number, LibraryDetailsEntry>
}

type MovieSortField =
  | 'dateAdded'
  | 'lastWatched'
  | 'userRating'
  | 'tmdbRating'
  | 'title'
  | 'year'
  | 'runtime'

type SeriesSortField =
  | 'lastUpdated'
  | 'userRating'
  | 'tmdbRating'
  | 'watchedCount'
  | 'title'
  | 'year'

type SortDir = 'asc' | 'desc'

const MOVIE_SORT_FIELDS: { value: MovieSortField; label: string; dirs: SortDir[] }[] = [
  { value: 'dateAdded', label: 'Date added', dirs: ['desc'] },
  { value: 'lastWatched', label: 'Last watched', dirs: ['desc'] },
  { value: 'userRating', label: 'My rating', dirs: ['desc', 'asc'] },
  { value: 'tmdbRating', label: 'TMDB rating', dirs: ['desc', 'asc'] },
  { value: 'title', label: 'Title', dirs: ['asc', 'desc'] },
  { value: 'year', label: 'Year', dirs: ['desc', 'asc'] },
  { value: 'runtime', label: 'Runtime', dirs: ['desc', 'asc'] },
]

const SERIES_SORT_FIELDS: { value: SeriesSortField; label: string; dirs: SortDir[] }[] = [
  { value: 'lastUpdated', label: 'Last updated', dirs: ['desc'] },
  { value: 'userRating', label: 'My rating', dirs: ['desc', 'asc'] },
  { value: 'tmdbRating', label: 'TMDB rating', dirs: ['desc', 'asc'] },
  { value: 'watchedCount', label: 'Watched count', dirs: ['desc'] },
  { value: 'title', label: 'Title', dirs: ['asc', 'desc'] },
  { value: 'year', label: 'Year', dirs: ['desc', 'asc'] },
]

const DIR_LABELS: Record<string, { asc: string; desc: string }> = {
  dateAdded: { desc: 'Newest first', asc: 'Oldest first' },
  lastWatched: { desc: 'Most recent', asc: 'Least recent' },
  lastUpdated: { desc: 'Newest first', asc: 'Oldest first' },
  userRating: { desc: 'Highest first', asc: 'Lowest first' },
  tmdbRating: { desc: 'Highest first', asc: 'Lowest first' },
  watchedCount: { desc: 'Most watched', asc: 'Least watched' },
  title: { asc: 'A \u2192 Z', desc: 'Z \u2192 A' },
  year: { desc: 'Newest first', asc: 'Oldest first' },
  runtime: { desc: 'Longest first', asc: 'Shortest first' },
}

function movieSortKeyFor(field: MovieSortField, dir: SortDir): MovieSortKey {
  switch (field) {
    case 'dateAdded':
      return 'dateAddedDesc'
    case 'lastWatched':
      return 'lastWatchedDesc'
    case 'userRating':
      return dir === 'asc' ? 'userRatingAsc' : 'userRatingDesc'
    case 'tmdbRating':
      return dir === 'asc' ? 'tmdbRatingAsc' : 'tmdbRatingDesc'
    case 'title':
      return dir === 'desc' ? 'titleDesc' : 'titleAsc'
    case 'year':
      return dir === 'asc' ? 'yearAsc' : 'yearDesc'
    case 'runtime':
      return dir === 'asc' ? 'runtimeAsc' : 'runtimeDesc'
  }
}

function seriesSortKeyFor(field: SeriesSortField, dir: SortDir): SeriesSortKey {
  switch (field) {
    case 'lastUpdated':
      return 'lastUpdatedDesc'
    case 'userRating':
      return dir === 'asc' ? 'userRatingAsc' : 'userRatingDesc'
    case 'tmdbRating':
      return dir === 'asc' ? 'tmdbRatingAsc' : 'tmdbRatingDesc'
    case 'watchedCount':
      return 'watchedCountDesc'
    case 'title':
      return dir === 'desc' ? 'titleDesc' : 'titleAsc'
    case 'year':
      return dir === 'asc' ? 'yearAsc' : 'yearDesc'
  }
}

function movieSortKeyDecompose(key: MovieSortKey): { field: MovieSortField; dir: SortDir } {
  switch (key) {
    case 'dateAddedDesc':
      return { field: 'dateAdded', dir: 'desc' }
    case 'lastWatchedDesc':
      return { field: 'lastWatched', dir: 'desc' }
    case 'userRatingDesc':
      return { field: 'userRating', dir: 'desc' }
    case 'userRatingAsc':
      return { field: 'userRating', dir: 'asc' }
    case 'tmdbRatingDesc':
      return { field: 'tmdbRating', dir: 'desc' }
    case 'tmdbRatingAsc':
      return { field: 'tmdbRating', dir: 'asc' }
    case 'titleAsc':
      return { field: 'title', dir: 'asc' }
    case 'titleDesc':
      return { field: 'title', dir: 'desc' }
    case 'yearDesc':
      return { field: 'year', dir: 'desc' }
    case 'yearAsc':
      return { field: 'year', dir: 'asc' }
    case 'runtimeDesc':
      return { field: 'runtime', dir: 'desc' }
    case 'runtimeAsc':
      return { field: 'runtime', dir: 'asc' }
  }
}

function seriesSortKeyDecompose(key: SeriesSortKey): { field: SeriesSortField; dir: SortDir } {
  switch (key) {
    case 'lastUpdatedDesc':
      return { field: 'lastUpdated', dir: 'desc' }
    case 'userRatingDesc':
      return { field: 'userRating', dir: 'desc' }
    case 'userRatingAsc':
      return { field: 'userRating', dir: 'asc' }
    case 'tmdbRatingDesc':
      return { field: 'tmdbRating', dir: 'desc' }
    case 'tmdbRatingAsc':
      return { field: 'tmdbRating', dir: 'asc' }
    case 'watchedCountDesc':
      return { field: 'watchedCount', dir: 'desc' }
    case 'titleAsc':
      return { field: 'title', dir: 'asc' }
    case 'titleDesc':
      return { field: 'title', dir: 'desc' }
    case 'yearDesc':
      return { field: 'year', dir: 'desc' }
    case 'yearAsc':
      return { field: 'year', dir: 'asc' }
  }
}

const SOURCE_OPTIONS: { value: LibraryFilters['source']; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'virtual', label: 'Streaming' },
  { value: 'physical', label: 'On a device' },
]

const RATING_STATUS_OPTIONS: {
  value: 'all' | 'rated' | 'unrated'
  label: string
}[] = [
  { value: 'all', label: 'All' },
  { value: 'rated', label: 'Rated' },
  { value: 'unrated', label: 'Unrated' },
]

const TMDB_RATING_OPTIONS = [0, 6, 7, 8, 9] as const

/**
 * Combined search + filter + sort bar for the library views. Ports the
 * desktop AdvancedFilterPanel + ModernFilterHeader filters to the PWA
 * at 1:1 fidelity - genre multi-select, year range, runtime range
 * (movies only), user + TMDB rating thresholds, decade buckets, source
 * and rating-status toggles. Composes with the sort dropdown and
 * direction toggle in the same header row.
 */
export function LibrarySearchFilterBar({
  scope,
  filters,
  onFiltersChange,
  movieSort,
  onMovieSortChange,
  seriesSort,
  onSeriesSortChange,
  detailsIndex,
}: Props) {
  const [expanded, setExpanded] = useState(false)

  const filtersDirty = isFiltersDirty(filters)
  const showPanel = expanded || filtersDirty

  const availableGenres = useMemo(() => {
    const set = new Set<string>()
    if (detailsIndex) {
      for (const entry of detailsIndex.values()) {
        for (const g of entry.genres) set.add(g)
      }
    }
    for (const g of filters.genres) set.add(g)
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [detailsIndex, filters.genres])

  const movieSortDecomposed = movieSortKeyDecompose(movieSort)
  const seriesSortDecomposed = seriesSortKeyDecompose(seriesSort)
  const activeSortField =
    scope === 'movies' ? movieSortDecomposed.field : seriesSortDecomposed.field
  const activeSortDir = scope === 'movies' ? movieSortDecomposed.dir : seriesSortDecomposed.dir
  const fieldOptions = scope === 'movies' ? MOVIE_SORT_FIELDS : SERIES_SORT_FIELDS
  const currentField = fieldOptions.find((f) => f.value === activeSortField)
  const allowedDirs: SortDir[] = currentField?.dirs ?? ['desc']

  const handleSortFieldChange = (nextField: string) => {
    if (scope === 'movies') {
      const fieldDef = MOVIE_SORT_FIELDS.find((f) => f.value === nextField)
      if (!fieldDef) return
      const dir = fieldDef.dirs.includes(movieSortDecomposed.dir)
        ? movieSortDecomposed.dir
        : fieldDef.dirs[0]
      onMovieSortChange(movieSortKeyFor(fieldDef.value, dir))
    } else {
      const fieldDef = SERIES_SORT_FIELDS.find((f) => f.value === nextField)
      if (!fieldDef) return
      const dir = fieldDef.dirs.includes(seriesSortDecomposed.dir)
        ? seriesSortDecomposed.dir
        : fieldDef.dirs[0]
      onSeriesSortChange(seriesSortKeyFor(fieldDef.value, dir))
    }
  }

  const handleSortDirChange = (dir: SortDir) => {
    if (scope === 'movies') {
      onMovieSortChange(movieSortKeyFor(movieSortDecomposed.field, dir))
    } else {
      onSeriesSortChange(seriesSortKeyFor(seriesSortDecomposed.field, dir))
    }
  }

  const handleClearFilters = () => {
    onFiltersChange({ ...DEFAULT_FILTERS, search: filters.search })
  }

  const update = (patch: Partial<LibraryFilters>) => {
    onFiltersChange({ ...filters, ...patch })
  }

  const toggleGenre = (g: string) => {
    const has = filters.genres.includes(g)
    update({ genres: has ? filters.genres.filter((x) => x !== g) : [...filters.genres, g] })
  }

  const toggleDecade = (value: number) => {
    const has = filters.decades.includes(value)
    update({
      decades: has ? filters.decades.filter((x) => x !== value) : [...filters.decades, value],
    })
  }

  const ratingStatus: 'all' | 'rated' | 'unrated' =
    filters.hasUserRating === undefined ? 'all' : filters.hasUserRating ? 'rated' : 'unrated'

  return (
    <div
      className={`mb-4 overflow-hidden rounded-lg border bg-bg-secondary transition-colors ${
        showPanel ? 'border-accent-primary/40 shadow-cinematic' : 'border-border-primary'
      }`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <SearchIcon />
        <input
          type="text"
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          onFocus={() => setExpanded(true)}
          placeholder="Search your library..."
          className="w-full bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => update({ search: '' })}
            className="shrink-0 text-[11px] text-text-tertiary hover:text-text-primary"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label="Toggle filters"
          className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors ${
            showPanel
              ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary'
              : 'border-border-primary bg-bg-tertiary text-text-tertiary hover:text-text-primary'
          }`}
        >
          Filters {filtersDirty && '\u00b7'}
        </button>
      </div>

      {showPanel && (
        <div className="space-y-4 border-t border-border-primary px-3 py-3">
          <Group label="Sort">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={activeSortField}
                onChange={(e) => handleSortFieldChange(e.target.value)}
                className="flex-1 min-w-[140px] rounded-md border border-border-primary bg-bg-tertiary px-3 py-2 text-sm text-text-primary focus:border-accent-primary focus:outline-none"
                aria-label="Sort field"
              >
                {fieldOptions.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <select
                value={activeSortDir}
                onChange={(e) => handleSortDirChange(e.target.value as SortDir)}
                disabled={allowedDirs.length < 2}
                className="rounded-md border border-border-primary bg-bg-tertiary px-3 py-2 text-sm text-text-primary focus:border-accent-primary focus:outline-none disabled:opacity-60"
                aria-label="Sort direction"
              >
                {allowedDirs.map((d) => (
                  <option key={d} value={d}>
                    {DIR_LABELS[activeSortField]?.[d] ??
                      (d === 'asc' ? 'Ascending' : 'Descending')}
                  </option>
                ))}
              </select>
            </div>
          </Group>

          <Group label="Source">
            <Pills
              value={filters.source}
              options={SOURCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => update({ source: v as LibraryFilters['source'] })}
            />
          </Group>

          <Group label="Rating status">
            <Pills
              value={ratingStatus}
              options={RATING_STATUS_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              onChange={(v) =>
                update({
                  hasUserRating: v === 'all' ? undefined : v === 'rated',
                })
              }
            />
          </Group>

          <Group label={`My rating ${filters.minUserRating}\u2013${filters.maxUserRating}`}>
            <DualRange
              min={0}
              max={10}
              step={1}
              from={filters.minUserRating}
              to={filters.maxUserRating}
              onChange={(from, to) => update({ minUserRating: from, maxUserRating: to })}
            />
          </Group>

          <Group label="Minimum TMDB rating">
            <Pills
              value={String(filters.minTmdbRating)}
              options={TMDB_RATING_OPTIONS.map((v) => ({
                value: String(v),
                label: v === 0 ? 'Any' : `${v}+`,
              }))}
              onChange={(v) => update({ minTmdbRating: parseInt(v, 10) })}
            />
          </Group>

          <Group
            label={`Year ${filters.yearFrom ?? YEAR_MIN}\u2013${filters.yearTo ?? YEAR_MAX}`}
          >
            <DualRange
              min={YEAR_MIN}
              max={YEAR_MAX}
              step={1}
              from={filters.yearFrom ?? YEAR_MIN}
              to={filters.yearTo ?? YEAR_MAX}
              onChange={(from, to) =>
                update({
                  yearFrom: from === YEAR_MIN ? undefined : from,
                  yearTo: to === YEAR_MAX ? undefined : to,
                })
              }
            />
          </Group>

          <Group label="Decade">
            <div className="flex flex-wrap gap-2">
              {DECADE_BUCKETS.map((d) => {
                const active = filters.decades.includes(d.value)
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDecade(d.value)}
                    className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                      active
                        ? 'bg-accent-primary text-black'
                        : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {d.label}
                  </button>
                )
              })}
            </div>
          </Group>

          {scope === 'movies' && (
            <Group
              label={`Runtime ${filters.runtimeFrom ?? RUNTIME_MIN}\u2013${
                filters.runtimeTo ?? RUNTIME_MAX
              } min`}
            >
              <DualRange
                min={RUNTIME_MIN}
                max={RUNTIME_MAX}
                step={5}
                from={filters.runtimeFrom ?? RUNTIME_MIN}
                to={filters.runtimeTo ?? RUNTIME_MAX}
                onChange={(from, to) =>
                  update({
                    runtimeFrom: from === RUNTIME_MIN ? undefined : from,
                    runtimeTo: to === RUNTIME_MAX ? undefined : to,
                  })
                }
              />
            </Group>
          )}

          <Group label="Genres">
            {availableGenres.length === 0 ? (
              <p className="text-[11px] text-text-tertiary">
                Genres load as TMDB metadata resolves.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableGenres.map((g) => {
                  const active = filters.genres.includes(g)
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => toggleGenre(g)}
                      className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                        active
                          ? 'bg-accent-primary text-black'
                          : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      {g}
                    </button>
                  )
                })}
              </div>
            )}
          </Group>

          <div className="flex items-center justify-end gap-2 pt-1">
            {filtersDirty && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="rounded-md bg-bg-tertiary px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-primary hover:text-text-primary"
              >
                Reset filters
              </button>
            )}
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="rounded-md bg-accent-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-accent-hover"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
        {label}
      </p>
      {children}
    </div>
  )
}

function Pills({
  value,
  options,
  onChange,
}: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
              active
                ? 'bg-accent-primary text-black'
                : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Dual-handle range picker built from two stacked native range inputs.
 * Native inputs keep the interaction pointer + touch friendly (key
 * requirement for PWA on phones) while still giving us both the lower
 * and upper bound. Clamps on change so the handles can not cross.
 */
function DualRange({
  min,
  max,
  step,
  from,
  to,
  onChange,
}: {
  min: number
  max: number
  step: number
  from: number
  to: number
  onChange: (from: number, to: number) => void
}) {
  const clampFrom = (v: number) => Math.min(Math.max(v, min), to)
  const clampTo = (v: number) => Math.max(Math.min(v, max), from)
  const pct = (v: number) => ((v - min) / (max - min)) * 100
  return (
    <div className="relative h-8">
      <div className="absolute top-1/2 h-1 w-full -translate-y-1/2 rounded-full bg-bg-tertiary" />
      <div
        className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-accent-primary"
        style={{ left: `${pct(from)}%`, width: `${pct(to) - pct(from)}%` }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={from}
        onChange={(e) => onChange(clampFrom(parseInt(e.target.value, 10)), to)}
        className="pointer-events-none absolute inset-0 h-full w-full appearance-none bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto"
        aria-label="Range minimum"
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={to}
        onChange={(e) => onChange(from, clampTo(parseInt(e.target.value, 10)))}
        className="pointer-events-none absolute inset-0 h-full w-full appearance-none bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto"
        aria-label="Range maximum"
      />
    </div>
  )
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-text-tertiary"
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}
